import { Card, TestData, TestDataLocale } from "@/types/linguadeck";
import { I18N_LANGUAGE_KEYS } from "@/i18n/i18n";

const PLACEHOLDER = "—";

/**
 * Generate test_data on the fly for a card that only has front/back (standard flashcard).
 * - Question: card.front
 * - Correct answer: card.back
 * - Distractors: 3 random backs from other cards in the deck (or placeholders if not enough)
 */
export function generateTestDataFromFlashcard(
  card: Card,
  allCards: Card[],
  _lang: string
): TestDataLocale {
  const question = (card.front || "").trim() || PLACEHOLDER;
  const correctAnswer = (card.back || "").trim() || PLACEHOLDER;

  const otherBacks = allCards
    .filter((c) => c !== card && (c.back || "").trim())
    .map((c) => (c.back || "").trim())
    .filter((b) => b && b !== correctAnswer);

  const uniqueBacks = [...new Set(otherBacks)];
  const shuffled = [...uniqueBacks].sort(() => Math.random() - 0.5);
  const distractors = shuffled.slice(0, 3);
  while (distractors.length < 3) {
    distractors.push(PLACEHOLDER);
  }

  const options = [correctAnswer, ...distractors];
  for (let i = options.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [options[i], options[j]] = [options[j], options[i]];
  }
  const correctIndex = Math.max(0, options.indexOf(correctAnswer));

  return { question, options, correctIndex, hint: card.hint };
}

/**
 * Get test data for a card: use existing testData if available, otherwise generate from front/back.
 */
export function getOrGenerateTestData(
  card: Card,
  allCards: Card[],
  lang: string
): TestDataLocale {
  const existing = getLocalizedTestData(card, lang);
  if (existing) return existing;
  return generateTestDataFromFlashcard(card, allCards, lang);
}

/**
 * Get localized test data for a card with smart fallback.
 * 1. Try requested lang, then fallback chain (en, then other I18N_LANGUAGE_KEYS), then any key in test_data.
 * 2. If test_data exists but no valid locale: synthesize from card (question=front, options=[back, —, —, —]).
 * 3. If no test_data at all: return null (card excluded from Test Mode).
 */
export function getLocalizedTestData(card: { front: string; back: string; hint?: string; testData?: TestData | null }, lang: string): TestDataLocale | null {
  const baseLang = (lang || "en").split("-")[0];
  const td = card.testData;

  if (td && typeof td === "object") {
    const fallbackOrder = [baseLang, "en", ...I18N_LANGUAGE_KEYS.filter((l) => l !== baseLang && l !== "en")];
    for (const l of fallbackOrder) {
      const loc = td[l];
      if (loc && typeof loc === "object" && typeof (loc as TestDataLocale).question === "string" && Array.isArray((loc as TestDataLocale).options) && (loc as TestDataLocale).options!.length >= 2) {
        const L = loc as TestDataLocale;
        const correctIndex = typeof L.correctIndex === "number" ? Math.max(0, Math.min(L.correctIndex, L.options.length - 1)) : 0;
        return { ...L, correctIndex };
      }
    }
    const anyKey = Object.keys(td)[0];
    const loc = anyKey ? td[anyKey] : null;
    if (loc && typeof loc === "object" && Array.isArray((loc as TestDataLocale).options) && (loc as TestDataLocale).options!.length >= 2) {
      const L = loc as TestDataLocale;
      const correctIndex = typeof L.correctIndex === "number" ? Math.max(0, Math.min(L.correctIndex, L.options.length - 1)) : 0;
      return { question: (L as TestDataLocale).question || card.front, options: L.options, correctIndex, hint: (L as TestDataLocale).hint };
    }
  }

  if (!td || typeof td !== "object") return null;

  const back = card.back || "—";
  return {
    question: card.front,
    options: [back, "—", "—", "—"],
    correctIndex: 0,
    hint: card.hint,
  };
}

/** Normalize test_data from AI. Accepts any language keys - iterates over supportedLangs. */
export function normalizeTestData(
  raw: unknown,
  supportedLangs: string[] = ["en", "ru", "es", "fr", "uk"]
): TestData | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const obj = raw as Record<string, unknown>;
  const result: TestData = {};
  for (const lang of supportedLangs) {
    const loc = obj[lang];
    if (!loc || typeof loc !== "object") continue;
    const L = loc as Record<string, unknown>;
    const question = typeof L.question === "string" ? L.question : "";
    const options = Array.isArray(L.options) ? L.options.map(String).slice(0, 4) : [];
    if (!question || options.length < 2) continue;
    const correctIndex =
      typeof L.correctIndex === "number"
        ? Math.max(0, Math.min(L.correctIndex, options.length - 1))
        : typeof (L as { correct?: number }).correct === "number"
          ? Math.max(0, Math.min((L as { correct: number }).correct, options.length - 1))
          : 0;
    const hint = typeof L.hint === "string" ? L.hint : undefined;
    result[lang] = { question, options, correctIndex, hint };
  }
  return Object.keys(result).length > 0 ? result : undefined;
}
